import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, X, ArrowUpRight } from "lucide-react";
import { Navbar1 } from "@/components/ui/navbar-1";
import { SiteFooter } from "@/components/SiteFooter";

const TITLE = "Alternativa brasileira ao Discogs | Viny Club";
const DESC =
  "Viny Club é a alternativa brasileira ao Discogs: catalogue sua coleção de vinil em português, escaneie discos no sebo, avalie faixa a faixa e monte o mini site da sua coleção. Compare os recursos.";
const URL = "https://viny.club/alternativa-ao-discogs";

type Row = {
  feature: string;
  detail: string;
  viny: boolean;
  discogs: boolean | "parcial";
};

const ROWS: Row[] = [
  {
    feature: "Interface 100% em português do Brasil",
    detail: "Nada de tradução automática ou termos em inglês para catalogar seus discos.",
    viny: true,
    discogs: false,
  },
  {
    feature: "Modo Garimpo para sebos e feiras",
    detail: "Escaneie o código de barras ou tire foto da capa e veja o disco na hora, no celular.",
    viny: true,
    discogs: "parcial",
  },
  {
    feature: "Reconhecimento da capa por IA",
    detail: "Sem código de barras? A foto da capa já identifica o álbum.",
    viny: true,
    discogs: false,
  },
  {
    feature: "Prévia das faixas antes de comprar",
    detail: "Ouça trechos direto no app enquanto está na loja física.",
    viny: true,
    discogs: false,
  },
  {
    feature: "Avaliação faixa a faixa",
    detail: "Dê nota para cada música; a nota do álbum é a mediana da comunidade.",
    viny: true,
    discogs: false,
  },
  {
    feature: "Mini site da sua coleção (link na bio)",
    detail: "viny.club/seu-user com templates prontos, pronto para o Instagram.",
    viny: true,
    discogs: false,
  },
  {
    feature: "Insights da coleção",
    detail: "Quanto você investiu, valor estimado, gêneros, décadas e raridades.",
    viny: true,
    discogs: false,
  },
  {
    feature: "Preços reais pagos pela comunidade brasileira",
    detail: "Referência em reais, do mercado daqui — não conversão de dólar.",
    viny: true,
    discogs: "parcial",
  },
  {
    feature: "Comunidade e perfis de colecionadores",
    detail: "Siga curadores, recomende discos e descubra por afinidade.",
    viny: true,
    discogs: false,
  },
  {
    feature: "Anúncios de venda e troca entre colecionadores",
    detail: "Fale direto com quem quer o disco, sem intermediário internacional.",
    viny: true,
    discogs: true,
  },
  {
    feature: "Catálogo global de lançamentos e prensagens",
    detail: "Base de dados de discos e edições para catalogar.",
    viny: true,
    discogs: true,
  },
  {
    feature: "Suporte e comunidade em português",
    detail: "Time brasileiro, atendimento em português.",
    viny: true,
    discogs: false,
  },
  {
    feature: "Uso gratuito das funções principais",
    detail: "Coleção ilimitada no plano grátis; Pro libera templates e extras.",
    viny: true,
    discogs: true,
  },
];

const FAQ = [
  {
    q: "O Viny Club substitui o Discogs?",
    a: "Para catalogar a coleção, garimpar discos em sebos e mostrar o que você tem, sim. O Viny Club é focado no colecionador brasileiro: interface em português, preços em reais e comunidade daqui. O Discogs continua sendo uma base global de prensagens e um marketplace internacional.",
  },
  {
    q: "Existe uma alternativa ao Discogs em português?",
    a: "Sim. O Viny Club é uma plataforma brasileira, com toda a experiência em português do Brasil, do cadastro do disco aos relatórios da coleção.",
  },
  {
    q: "Consigo catalogar minha coleção de vinil de graça?",
    a: "Sim. A coleção é ilimitada no plano gratuito. O plano Pro libera templates extras do mini site e recursos avançados.",
  },
  {
    q: "Como funciona o Modo Garimpo?",
    a: "No sebo ou na feira, você abre o scanner, lê o código de barras ou tira uma foto da capa. O disco é identificado na hora e você ainda ouve uma prévia das faixas antes de decidir.",
  },
];

export const Route = createFileRoute("/alternativa-ao-discogs")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      {
        name: "keywords",
        content:
          "alternativa ao discogs, alternativa brasileira ao discogs, discogs em português, catalogar coleção de vinil, app para colecionadores de vinil",
      },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: DiscogsAlternative,
});

function Mark({ value }: { value: boolean | "parcial" }) {
  if (value === "parcial") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60" />
        Parcial
      </span>
    );
  }
  return value ? (
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#9231FF]/12 text-[#9231FF]">
      <Check className="h-4 w-4" strokeWidth={2.5} />
      <span className="sr-only">Sim</span>
    </span>
  ) : (
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-foreground/[0.06] text-muted-foreground">
      <X className="h-4 w-4" strokeWidth={2.5} />
      <span className="sr-only">Não</span>
    </span>
  );
}

function DiscogsAlternative() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar1 />

      <main className="mx-auto max-w-5xl px-6 pt-28 pb-20">
        <header className="max-w-3xl">
          <p className="mb-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">
            Comparativo
          </p>
          <h1 className="font-display text-[clamp(2.25rem,5vw,3.5rem)] font-medium leading-[1.02] tracking-[-0.03em]">
            Alternativa brasileira do Discogs
          </h1>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground">
            O Viny Club é feito para o colecionador brasileiro: catalogue sua coleção de vinil
            em português, garimpe discos com o scanner no sebo, avalie faixa a faixa e mostre
            tudo no mini site da sua coleção. Veja o que temos que o Discogs não tem.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to="/auth"
              className="group inline-flex items-center gap-2 rounded-full bg-[#9231FF] px-7 py-3 text-sm font-bold text-white transition-transform hover:scale-105"
            >
              Comece grátis
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
            <span className="text-[13px] text-muted-foreground">
              Coleção ilimitada no plano gratuito.
            </span>
          </div>
        </header>

        {/* Tabela comparativa */}
        <section className="mt-14" aria-labelledby="comparativo">
          <h2
            id="comparativo"
            className="text-xl font-medium tracking-tight sm:text-2xl"
          >
            Viny Club x Discogs, recurso por recurso
          </h2>

          <div className="mt-6 overflow-hidden rounded-2xl border border-border">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-foreground/[0.03]">
                  <th
                    scope="col"
                    className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground sm:px-6"
                  >
                    Recurso
                  </th>
                  <th
                    scope="col"
                    className="w-[92px] px-3 py-3 text-center text-xs font-semibold uppercase tracking-[0.14em] text-[#9231FF] sm:w-[140px]"
                  >
                    Viny Club
                  </th>
                  <th
                    scope="col"
                    className="w-[92px] px-3 py-3 text-center text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground sm:w-[140px]"
                  >
                    Discogs
                  </th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r) => (
                  <tr key={r.feature} className="border-t border-border align-top">
                    <th scope="row" className="px-4 py-4 font-normal sm:px-6">
                      <span className="block text-sm font-medium leading-snug">
                        {r.feature}
                      </span>
                      <span className="mt-1 block text-[13px] leading-relaxed text-muted-foreground">
                        {r.detail}
                      </span>
                    </th>
                    <td className="px-3 py-4 text-center">
                      <Mark value={r.viny} />
                    </td>
                    <td className="px-3 py-4 text-center">
                      <Mark value={r.discogs} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
            Comparativo elaborado pela equipe do Viny Club com base nos recursos públicos das
            duas plataformas. Discogs é uma marca de terceiros e não tem relação com o Viny Club.
          </p>
        </section>

        {/* FAQ */}
        <section className="mt-16" aria-labelledby="faq">
          <h2 id="faq" className="text-xl font-medium tracking-tight sm:text-2xl">
            Perguntas frequentes
          </h2>
          <div className="mt-6 divide-y divide-border border-y border-border">
            {FAQ.map((f) => (
              <div key={f.q} className="py-5">
                <h3 className="text-sm font-medium">{f.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16 rounded-2xl border border-border bg-foreground/[0.03] px-6 py-10 text-center">
          <h2 className="text-xl font-medium tracking-tight sm:text-2xl">
            Traga sua coleção para uma plataforma brasileira
          </h2>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
            Catalogue seus discos, garimpe com o scanner e mostre tudo no seu link na bio.
          </p>
          <Link
            to="/auth"
            className="group mt-6 inline-flex items-center gap-2 rounded-full bg-[#9231FF] px-7 py-3 text-sm font-bold text-white transition-transform hover:scale-105"
          >
            Criar minha conta grátis
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
